<template>
  <body data-sidebar="dark">
  <!-- Begin page -->
  <div id="layout-wrapper">
    <!-- Left Sidebar Navigation -->
    <MemberPortalNavigation />

    <!-- Main Content -->
    <div class="content-minimize">
      <div class="page-content">
        <div class="container-fluid">
          <!-- Page Title -->
          <div class="row">
            <div class="col-12">
              <div class="page-title-box d-sm-flex align-items-center justify-content-between">
                <h4 class="mb-sm-0 font-size-18"></h4>
                <div class="page-title-right">
                  <ol class="breadcrumb m-0">
                    <li class="breadcrumb-item active"></li>
                  </ol>
                </div>
              </div>
            </div>
          </div>

          <!-- Welcome Banner -->
          <div class="row">
            <div class="col-12">
              <div class="card welcome-card bg-primary bg-gradient overflow-hidden">
                <div class="card-body">
                  <div class="row align-items-center">
                    <div class="col-auto">
                      <div class="position-relative">
                        <img v-if="hasProfilePhoto"
                             :src="profilePictureUrl"
                             class="rounded-circle border border-3 border-white shadow"
                             style="width: 80px; height: 80px; object-fit: cover;"
                             @error="handleImageError"
                             alt="Profile">
                        <div v-else
                             class="bg-white rounded-circle d-flex align-items-center justify-content-center shadow"
                             style="width: 80px; height: 80px;">
                          <span class="text-primary fw-bold" style="font-size: 1.8rem;">{{ getInitials(profile.full_name) }}</span>
                        </div>
                        <span class="position-absolute bottom-0 end-0 bg-success rounded-circle border border-2 border-white"
                              style="width: 18px; height: 18px;"
                              v-if="profile.status === 'active'"></span>
                      </div>
                    </div>
                    <div class="col">
                      <h4 class="text-white mb-1">Welcome, {{ profile.full_name }}!</h4>
                      <p class="text-white-50 mb-2">Your CBHI Member Portal</p>
                      <div class="d-flex flex-wrap gap-3">
                        <span class="badge bg-white bg-opacity-25 text-white px-3 py-2">
                          <i class="bx bx-id-card me-1"></i>{{ profile.cbhi_number }}
                        </span>
                        <span class="badge bg-success px-3 py-2" v-if="profile.status === 'active'">
                          <i class="bx bx-check-circle me-1"></i>Active Member
                        </span>
                        <span class="badge bg-warning px-3 py-2" v-else-if="profile.status === 'suspended'">
                          <i class="bx bx-pause-circle me-1"></i>Suspended
                        </span>
                        <span class="badge bg-danger px-3 py-2" v-else>
                          <i class="bx bx-x-circle me-1"></i>{{ capitalizeFirst(profile.status) }}
                        </span>
                      </div>
                    </div>
                    <div class="col-auto d-none d-md-block">
                      <img :src="'./assets/images/health-insurance.svg'" alt="" style="height: 120px; opacity: 0.3;">
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <!-- Loading State -->
          <div v-if="loading" class="text-center py-5">
            <div class="spinner-border text-primary" role="status">
              <span class="visually-hidden">Loading...</span>
            </div>
            <p class="mt-3 text-muted">Loading your dashboard...</p>
          </div>

          <!-- Dashboard Content -->
          <div v-else>
            <!-- Coverage Status Alert -->
            <div class="row" v-if="showRenewalAlert">
              <div class="col-12">
                <div class="alert alert-warning alert-dismissible fade show d-flex align-items-center" role="alert">
                  <i class="bx bx-error-circle fs-3 me-3"></i>
                  <div class="flex-grow-1">
                    <strong>Coverage Expiring Soon!</strong>
                    Your CBHI coverage will expire in <strong>{{ daysUntilExpiry }} days</strong> on {{ formatDate(profile.expiry_date) }}.
                    <router-link to="/payment-make" class="alert-link ms-2">Renew Now</router-link>
                  </div>
                  <button type="button" class="btn-close" data-bs-dismiss="alert"></button>
                </div>
              </div>
            </div>

            <!-- Stats Cards Row -->
            <div class="row">
              <!-- Coverage Status Card -->
              <div class="col-xl-3 col-md-6">
                <div class="card stat-card">
                  <div class="card-body">
                    <div class="d-flex align-items-center">
                      <div class="flex-shrink-0">
                        <div class="stat-icon bg-soft-success">
                          <i class="bx bx-shield-check text-success"></i>
                        </div>
                      </div>
                      <div class="flex-grow-1 ms-3">
                        <p class="text-muted mb-1 fw-medium">Coverage Status</p>
                        <h5 class="mb-0" :class="profile.status === 'active' ? 'text-success' : 'text-danger'">
                          {{ profile.status === 'active' ? 'Active' : capitalizeFirst(profile.status) }}
                        </h5>
                        <small class="text-muted">
                          <i class="bx bx-calendar me-1"></i>
                          Expires: {{ formatDate(profile.expiry_date) }}
                        </small>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <!-- Beneficiaries Card -->
              <div class="col-xl-3 col-md-6">
                <div class="card stat-card">
                  <div class="card-body">
                    <div class="d-flex align-items-center">
                      <div class="flex-shrink-0">
                        <div class="stat-icon bg-soft-primary">
                          <i class="bx bx-group text-primary"></i>
                        </div>
                      </div>
                      <div class="flex-grow-1 ms-3">
                        <p class="text-muted mb-1 fw-medium">Covered Members</p>
                        <h5 class="mb-0">{{ totalBeneficiaries }}</h5>
                        <small class="text-muted">
                          1 Primary + {{ beneficiaryCount }} Dependents
                        </small>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <!-- Annual Premium Card -->
              <div class="col-xl-3 col-md-6">
                <div class="card stat-card">
                  <div class="card-body">
                    <div class="d-flex align-items-center">
                      <div class="flex-shrink-0">
                        <div class="stat-icon bg-soft-info">
                          <i class="bx bx-money text-info"></i>
                        </div>
                      </div>
                      <div class="flex-grow-1 ms-3">
                        <p class="text-muted mb-1 fw-medium">Annual Premium</p>
                        <h5 class="mb-0">{{ formatCurrency(profile.annual_premium) }}</h5>
                        <small :class="paymentStatusClass">
                          <i class="bx me-1" :class="paymentStatusIcon"></i>
                          {{ paymentStatusText }}
                        </small>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <!-- Days Until Expiry Card -->
              <div class="col-xl-3 col-md-6">
                <div class="card stat-card">
                  <div class="card-body">
                    <div class="d-flex align-items-center">
                      <div class="flex-shrink-0">
                        <div class="stat-icon" :class="daysUntilExpiry <= 30 ? 'bg-soft-danger' : daysUntilExpiry <= 60 ? 'bg-soft-warning' : 'bg-soft-success'">
                          <i class="bx bx-time-five" :class="daysUntilExpiry <= 30 ? 'text-danger' : daysUntilExpiry <= 60 ? 'text-warning' : 'text-success'"></i>
                        </div>
                      </div>
                      <div class="flex-grow-1 ms-3">
                        <p class="text-muted mb-1 fw-medium">Days Remaining</p>
                        <h5 class="mb-0" :class="daysLeftClass">{{ daysUntilExpiry }}</h5>
                        <small class="text-muted">
                          Until coverage expires
                        </small>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <!-- Main Content Row -->
            <div class="row">
              <!-- Left Column -->
              <div class="col-xl-8">
                <!-- Plan Information Card -->
                <div class="card">
                  <div class="card-header bg-transparent d-flex justify-content-between align-items-center">
                    <h5 class="card-title mb-0">
                      <i class="bx bx-shield-check text-primary me-2"></i>Your Health Plan
                    </h5>
                    <router-link to="/member-coverage" class="btn btn-soft-primary btn-sm">
                      View Details <i class="bx bx-right-arrow-alt ms-1"></i>
                    </router-link>
                  </div>
                  <div class="card-body">
                    <div class="row align-items-center">
                      <div class="col-md-8">
                        <div class="d-flex align-items-center mb-3">
                          <div class="plan-badge me-3" :class="getPlanBadgeClass(planInfo.plan_type)">
                            {{ planInfo.plan_type ? planInfo.plan_type.toUpperCase() : 'BASIC' }}
                          </div>
                          <div>
                            <h5 class="mb-0">{{ planInfo.plan_name || 'CBHI Standard Plan' }}</h5>
                            <p class="text-muted mb-0 small">{{ planInfo.category_name || 'Basic Coverage' }}</p>
                          </div>
                        </div>
                        <p class="text-muted mb-3">{{ planInfo.plan_description || 'Comprehensive health coverage for you and your family.' }}</p>

                        <!-- Coverage Features -->
                        <div class="row g-2">
                          <div class="col-6">
                            <div class="coverage-feature">
                              <i class="bx bx-check-circle text-success"></i>
                              <span>Outpatient Care</span>
                            </div>
                          </div>
                          <div class="col-6">
                            <div class="coverage-feature">
                              <i class="bx bx-check-circle text-success"></i>
                              <span>Inpatient Care</span>
                            </div>
                          </div>
                          <div class="col-6">
                            <div class="coverage-feature">
                              <i class="bx bx-check-circle text-success"></i>
                              <span>Emergency Services</span>
                            </div>
                          </div>
                          <div class="col-6">
                            <div class="coverage-feature">
                              <i class="bx bx-check-circle text-success"></i>
                              <span>Essential Medicines</span>
                            </div>
                          </div>
                          <div class="col-6">
                            <div class="coverage-feature">
                              <i class="bx bx-check-circle text-success"></i>
                              <span>Maternal Health</span>
                            </div>
                          </div>
                          <div class="col-6">
                            <div class="coverage-feature">
                              <i class="bx bx-check-circle text-success"></i>
                              <span>Immunizations</span>
                            </div>
                          </div>
                        </div>
                      </div>
                      <div class="col-md-4">
                        <div class="premium-box text-center">
                          <p class="text-muted mb-1 small">Annual Premium</p>
                          <h3 class="text-primary mb-2">{{ formatCurrency(profile.annual_premium) }}</h3>
                          <p class="text-muted mb-3 small">
                            Covering {{ totalBeneficiaries }} members
                          </p>
                          <router-link to="/payment-make" class="btn btn-primary btn-sm w-100" v-if="remainingBalance > 0">
                            <i class="bx bx-credit-card me-1"></i>Pay Now
                          </router-link>
                          <span v-else class="badge bg-success px-3 py-2">
                            <i class="bx bx-check me-1"></i>Fully Paid
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                <!-- Recent Activity Card -->
                <div class="card">
                  <div class="card-header bg-transparent d-flex justify-content-between align-items-center">
                    <h5 class="card-title mb-0">
                      <i class="bx bx-history text-primary me-2"></i>Recent Activity
                    </h5>
                    <router-link to="/payment-history" class="btn btn-soft-primary btn-sm">
                      View All <i class="bx bx-right-arrow-alt ms-1"></i>
                    </router-link>
                  </div>
                  <div class="card-body">
                    <!-- Activity Timeline -->
                    <div v-if="recentActivities.length > 0" class="activity-timeline">
                      <div class="activity-item" v-for="(activity, index) in recentActivities" :key="index">
                        <div class="activity-icon" :class="getActivityIconClass(activity.type)">
                          <i :class="getActivityIcon(activity.type)"></i>
                        </div>
                        <div class="activity-content">
                          <h6 class="mb-1">{{ activity.title }}</h6>
                          <p class="text-muted mb-0 small">{{ activity.description }}</p>
                          <small class="text-muted">{{ formatDate(activity.date) }}</small>
                        </div>
                        <div class="activity-amount" v-if="activity.amount">
                          <span :class="activity.type === 'payment' ? 'text-success' : 'text-info'">
                            {{ formatCurrency(activity.amount) }}
                          </span>
                        </div>
                      </div>
                    </div>

                    <!-- No Activity -->
                    <div v-else class="text-center py-4">
                      <i class="bx bx-calendar-x text-muted" style="font-size: 3rem;"></i>
                      <p class="text-muted mt-2 mb-0">No recent activity</p>
                    </div>
                  </div>
                </div>
              </div>

              <!-- Right Column -->
              <div class="col-xl-4">

                <!-- Membership Card Preview -->
                <div class="card membership-card-preview">
                  <div class="card-body p-0">
                    <div class="membership-card-inner">
                      <div class="card-header-section">
                        <div class="d-flex justify-content-between align-items-start">
                          <div>
                            <p class="mb-0 small text-white-50">Ministry of Health</p>
                          </div>
                          <span class="badge bg-white bg-opacity-25">CBHI</span>
                        </div>
                      </div>
                      <div class="card-body-section">
                        <div class="d-flex align-items-center mb-3">
                          <img v-if="hasProfilePhoto"
                               :src="profilePictureUrl"
                               class="rounded-circle border border-2 border-white me-3"
                               style="width: 50px; height: 50px; object-fit: cover;"
                               @error="handleImageError">
                          <div v-else
                               class="bg-white rounded-circle d-flex align-items-center justify-content-center me-3"
                               style="width: 50px; height: 50px;">
                            <span class="text-primary fw-bold">{{ getInitials(profile.full_name) }}</span>
                          </div>
                          <div>
                            <h6 class="text-white mb-0">{{ profile.full_name }}</h6>
                            <small class="text-white-50">{{ profile.cbhi_number }}</small>
                          </div>
                        </div>
                        <div class="row text-white small">
                          <div class="col-6">
                            <p class="mb-0 text-white-50">Valid Until</p>
                            <p class="mb-0 fw-medium">{{ formatDate(profile.expiry_date) }}</p>
                          </div>
                          <div class="col-6 text-end">
                            <p class="mb-0 text-white-50">Status</p>
                            <p class="mb-0 fw-medium">{{ capitalizeFirst(profile.status) }}</p>
                          </div>
                        </div>
                      </div>
                    </div>
                    <div class="text-center py-2">
                      <router-link to="/my-member-card" class="btn btn-link btn-sm text-primary">
                        <i class="bx bx-expand me-1"></i>View Full Card
                      </router-link>
                    </div>
                  </div>
                </div>

                <!-- Beneficiaries Summary Card -->
                <div class="card">
                  <div class="card-header bg-transparent d-flex justify-content-between align-items-center">
                    <h5 class="card-title mb-0">
                      <i class="bx bx-group text-primary me-2"></i>My Beneficiaries
                    </h5>
                    <router-link to="/beneficiaries-add" class="btn btn-soft-success btn-sm d-none">
                      <i class="bx bx-plus"></i>
                    </router-link>
                  </div>
                  <div class="card-body">
                    <!-- Primary Member -->
                    <div class="beneficiary-item mb-2">
                      <div class="d-flex align-items-center">
                        <img v-if="hasProfilePhoto"
                             :src="profilePictureUrl"
                             class="rounded-circle me-2"
                             style="width: 40px; height: 40px; object-fit: cover;"
                             @error="handleImageError">
                        <div v-else
                             class="bg-primary rounded-circle d-flex align-items-center justify-content-center me-2"
                             style="width: 40px; height: 40px;">
                          <span class="text-white small">{{ getInitials(profile.full_name) }}</span>
                        </div>
                        <div class="flex-grow-1">
                          <h6 class="mb-0 small">{{ profile.full_name }}</h6>
                          <small class="text-muted">Primary Member</small>
                        </div>
                        <span class="badge bg-primary">Self</span>
                      </div>
                    </div>

                    <!-- Beneficiaries List -->
                    <div v-for="beneficiary in beneficiaries.slice(0, 3)" :key="beneficiary.beneficiary_id" class="beneficiary-item mb-2">
                      <div class="d-flex align-items-center">
                        <img v-if="beneficiaryHasPhoto(beneficiary)"
                             :src="getBeneficiaryPhotoUrl(beneficiary)"
                             class="rounded-circle me-2"
                             style="width: 40px; height: 40px; object-fit: cover;"
                             @error="onBeneficiaryImageError($event, beneficiary)">
                        <div v-else
                             class="bg-secondary rounded-circle d-flex align-items-center justify-content-center me-2"
                             style="width: 40px; height: 40px;">
                          <span class="text-white small">{{ getInitials(beneficiary.full_name) }}</span>
                        </div>
                        <div class="flex-grow-1">
                          <h6 class="mb-0 small">{{ beneficiary.full_name }}</h6>
                          <small class="text-muted">{{ capitalizeFirst(beneficiary.relationship) }}</small>
                        </div>
                        <span class="badge" :class="beneficiary.status === 'active' ? 'bg-success' : 'bg-warning'">
                          {{ capitalizeFirst(beneficiary.status) }}
                        </span>
                      </div>
                    </div>

                    <!-- View All Link -->
                    <div v-if="beneficiaries.length > 3" class="text-center mt-3">
                      <router-link to="/beneficiaries-list" class="btn btn-outline-primary btn-sm">
                        View All {{ beneficiaries.length }} Beneficiaries
                      </router-link>
                    </div>

                    <!-- No Beneficiaries -->
                    <div v-if="beneficiaries.length === 0" class="text-center py-3">
                      <p class="text-muted small mb-2">No dependents added yet</p>
                      <router-link to="/beneficiaries-add" class="btn btn-outline-primary btn-sm">
                        <i class="bx bx-plus me-1"></i>Add Beneficiary
                      </router-link>
                    </div>
                  </div>
                </div>

                <!-- Last Payment Card -->
                <div class="card" v-if="lastPayment">
                  <div class="card-header bg-transparent">
                    <h5 class="card-title mb-0">
                      <i class="bx bx-receipt text-primary me-2"></i>Last Payment
                    </h5>
                  </div>
                  <div class="card-body">
                    <div class="d-flex justify-content-between mb-2">
                      <span class="text-muted">Amount</span>
                      <strong class="text-success">{{ formatCurrency(lastPayment.amount) }}</strong>
                    </div>
                    <div class="d-flex justify-content-between mb-2">
                      <span class="text-muted">Date</span>
                      <span>{{ formatDate(lastPayment.payment_date) }}</span>
                    </div>
                    <div class="d-flex justify-content-between mb-2">
                      <span class="text-muted">Reference</span>
                      <span class="small">{{ lastPayment.payment_reference }}</span>
                    </div>
                    <div class="d-flex justify-content-between">
                      <span class="text-muted">Year</span>
                      <span>{{ lastPayment.payment_year }}</span>
                    </div>
                    <hr>
                    <router-link to="/payment-history" class="btn btn-outline-primary btn-sm w-100">
                      <i class="bx bx-history me-1"></i>View Payment History
                    </router-link>
                  </div>
                </div>
              </div>
            </div>
          </div>

        </div>
      </div>
    </div>
  </div>
  </body>
</template>

<script>
/* eslint-disable */
import MemberPortalNavigation from "@/components/member-portal/MemberPortalNavigation.vue";
import { memberUrl, executeJson } from "@/api";

export default {
  name: "MemberDashboard",
  components: {
    MemberPortalNavigation
  },

  data() {
    return {
      loading: false,

      // Profile data
      profile: {},
      healthProfile: {},
      planInfo: {},
      beneficiaries: [],

      // Stats
      beneficiaryCount: 0,
      lastPayment: null,
      totalPaidThisYear: 0,

      // Recent activities
      recentActivities: []
    };
  },

  computed: {
    // Photo URL
    profilePictureUrl() {
      if (this.profile.profile_photo) {
        if (this.profile.profile_photo.startsWith('http')) {
          return this.profile.profile_photo;
        }
        return `https://rcmsuat.craftsilicon.com/api1/uploads/member-photos/${this.profile.profile_photo}`;
      }
      return './assets/images/users/default-avatar.png';
    },

    hasProfilePhoto() {
      return this.profile && this.profile.profile_photo;
    },

    // Total beneficiaries including primary member
    totalBeneficiaries() {
      return 1 + parseInt(this.beneficiaryCount || 0);
    },

    // Days until expiry
    daysUntilExpiry() {
      if (!this.profile.expiry_date) return 0;
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      const expiry = new Date(this.profile.expiry_date);
      const diffTime = expiry - today;
      const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
      return diffDays > 0 ? diffDays : 0;
    },

    // Days left class
    daysLeftClass() {
      if (this.daysUntilExpiry <= 30) return 'text-danger fw-bold';
      if (this.daysUntilExpiry <= 60) return 'text-warning fw-bold';
      return 'text-success fw-bold';
    },

    // Show renewal alert
    showRenewalAlert() {
      return this.daysUntilExpiry <= 30 && this.daysUntilExpiry > 0;
    },

    // Payment status
    remainingBalance() {
      const annual = parseFloat(this.profile.annual_premium) || 0;
      const paid = parseFloat(this.totalPaidThisYear) || 0;
      return annual - paid;
    },

    paymentStatusText() {
      if (this.remainingBalance <= 0) return 'Fully Paid';
      return `${this.formatCurrency(this.remainingBalance)} Due`;
    },

    paymentStatusClass() {
      return this.remainingBalance <= 0 ? 'text-success' : 'text-warning';
    },

    paymentStatusIcon() {
      return this.remainingBalance <= 0 ? 'bx-check-circle' : 'bx-error-circle';
    }
  },

  mounted() {
    this.loadDashboardData();
  },

  methods: {
    // Load all dashboard data
    async loadDashboardData() {
      this.loading = true;

      try {
        await Promise.all([
          this.loadMemberProfile(),
          this.loadBeneficiaries()
        ]);

        // Build recent activities from data
        this.buildRecentActivities();
      } catch (error) {
        console.error('Error loading dashboard:', error);
      } finally {
        this.loading = false;
      }
    },

    // Load member profile
    async loadMemberProfile() {
      const data = {
        function: "getMyMemberProfile"
      };

      try {
        const res = await executeJson(data, memberUrl);

        if (res.data.success) {
          this.profile = res.data.data.profile || {};
          this.healthProfile = res.data.data.healthProfile || {};
          this.planInfo = res.data.data.planInfo || {};
          this.beneficiaryCount = res.data.data.beneficiaryCount || 0;
          this.lastPayment = res.data.data.lastPayment || null;
          this.totalPaidThisYear = res.data.data.totalPaidThisYear || 0;
        }
      } catch (error) {
        console.error('Error loading profile:', error);
      }
    },

    // Load beneficiaries
    async loadBeneficiaries() {
      const data = {
        function: "getMyBeneficiaries"
      };

      try {
        const res = await executeJson(data, memberUrl);

        if (res.data.success) {
          this.beneficiaries = res.data.data.beneficiaries || [];
        }
      } catch (error) {
        console.error('Error loading beneficiaries:', error);
      }
    },

    // Build recent activities
    buildRecentActivities() {
      const activities = [];

      // Add registration
      if (this.profile.registration_date) {
        activities.push({
          type: 'registration',
          title: 'CBHI Registration',
          description: 'Successfully registered as CBHI member',
          date: this.profile.registration_date
        });
      }

      // Add last payment
      if (this.lastPayment) {
        activities.push({
          type: 'payment',
          title: 'Premium Payment',
          description: `Payment for year ${this.lastPayment.payment_year}`,
          date: this.lastPayment.payment_date,
          amount: this.lastPayment.amount
        });
      }

      // Add card issuance
      if (this.profile.cbhi_card_issued === '1' && this.profile.card_issue_date) {
        activities.push({
          type: 'card',
          title: 'CBHI Card Issued',
          description: 'Membership card was issued',
          date: this.profile.card_issue_date
        });
      }

      // Sort by date descending
      activities.sort((a, b) => new Date(b.date) - new Date(a.date));

      this.recentActivities = activities.slice(0, 5);
    },

    // Get activity icon
    getActivityIcon(type) {
      const icons = {
        'payment': 'bx bx-credit-card',
        'registration': 'bx bx-user-plus',
        'card': 'bx bx-id-card',
        'claim': 'bx bx-file',
        'beneficiary': 'bx bx-user-plus'
      };
      return icons[type] || 'bx bx-info-circle';
    },

    // Get activity icon class
    getActivityIconClass(type) {
      const classes = {
        'payment': 'bg-soft-success',
        'registration': 'bg-soft-primary',
        'card': 'bg-soft-info',
        'claim': 'bg-soft-warning',
        'beneficiary': 'bg-soft-secondary'
      };
      return classes[type] || 'bg-soft-secondary';
    },

    // Get plan badge class
    getPlanBadgeClass(planType) {
      if (!planType) return 'plan-badge-basic';
      const classes = {
        'basic': 'plan-badge-basic',
        'standard': 'plan-badge-standard',
        'premium': 'plan-badge-premium',
        'family': 'plan-badge-family'
      };
      return classes[planType.toLowerCase()] || 'plan-badge-basic';
    },

    // Image error handler
    handleImageError(event) {
      event.target.style.display = 'none';
      this.profile.profile_photo = null;
    },

    // Beneficiary photo methods
    getBeneficiaryPhotoUrl(beneficiary) {
      if (beneficiary.profile_photo) {
        if (beneficiary.profile_photo.startsWith('http')) {
          return beneficiary.profile_photo;
        }
        return `https://api.craftcollect.africa/cbhi/uploads/beneficiary-photos/${beneficiary.profile_photo}`;
      }
      return null;
    },

    beneficiaryHasPhoto(beneficiary) {
      return beneficiary && beneficiary.profile_photo;
    },

    onBeneficiaryImageError(event, beneficiary) {
      event.target.style.display = 'none';
      const index = this.beneficiaries.findIndex(b => b.beneficiary_id === beneficiary.beneficiary_id);
      if (index !== -1) {
        this.beneficiaries[index].profile_photo = null;
      }
    },

    // Utility methods
    getInitials(name) {
      if (!name) return '?';
      const parts = name.trim().split(' ');
      if (parts.length >= 2) {
        return (parts[0][0] + parts[1][0]).toUpperCase();
      }
      return name.substring(0, 2).toUpperCase();
    },

    formatDate(date) {
      if (!date) return 'N/A';
      return new Date(date).toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric'
      });
    },

    formatCurrency(amount) {
      if (!amount) return '0';
      return new Intl.NumberFormat().format(amount);
    },

    capitalizeFirst(str) {
      if (!str) return '';
      return str.charAt(0).toUpperCase() + str.slice(1).replace(/_/g, ' ');
    }
  }
};
</script>

<style scoped>
/* Content area with sidebar */
.content-minimize {
  margin-left: 250px;
  transition: margin-left 0.3s ease;
}

.page-content {
  padding: 70px 0 60px 0;
  min-height: 100vh;
  background-color: #f8f9fa;
}

@media (max-width: 992px) {
  .content-minimize {
    margin-left: 0 !important;
  }
}

.vertical-collpsed .content-minimize {
  margin-left: 70px;
}

/* Welcome Card */
.welcome-card {
  border: none;
  border-radius: 1rem;
  overflow: hidden;
}

.welcome-card .card-body {
  padding: 1.5rem;
}

/* Stat Cards */
.stat-card {
  border: none;
  border-radius: 0.75rem;
  box-shadow: 0 0.125rem 0.25rem rgba(0, 0, 0, 0.075);
  transition: all 0.3s ease;
}

.stat-card:hover {
  transform: translateY(-3px);
  box-shadow: 0 0.5rem 1rem rgba(0, 0, 0, 0.1);
}

.stat-icon {
  width: 50px;
  height: 50px;
  border-radius: 0.75rem;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1.5rem;
}

.bg-soft-success {
  background-color: rgba(40, 167, 69, 0.15);
}

.bg-soft-primary {
  background-color: rgba(44, 90, 160, 0.15);
}

.bg-soft-info {
  background-color: rgba(23, 162, 184, 0.15);
}

.bg-soft-warning {
  background-color: rgba(255, 193, 7, 0.15);
}

.bg-soft-danger {
  background-color: rgba(220, 53, 69, 0.15);
}

.bg-soft-secondary {
  background-color: rgba(108, 117, 125, 0.15);
}

/* Cards */
.card {
  border: none;
  border-radius: 0.75rem;
  box-shadow: 0 0.125rem 0.25rem rgba(0, 0, 0, 0.075);
}

.card-header {
  background: transparent;
  border-bottom: 1px solid #e9ecef;
  padding: 1rem 1.25rem;
}

.card-title {
  font-size: 1rem;
  font-weight: 600;
  color: #1e3f70;
  margin: 0;
}

/* Plan Badge */
.plan-badge {
  padding: 0.5rem 1rem;
  border-radius: 0.5rem;
  font-size: 0.75rem;
  font-weight: 700;
  letter-spacing: 0.5px;
}

.plan-badge-basic {
  background: linear-gradient(135deg, #6c757d 0%, #adb5bd 100%);
  color: white;
}

.plan-badge-standard {
  background: linear-gradient(135deg, #2c5aa0 0%, #4a90e2 100%);
  color: white;
}

.plan-badge-premium {
  background: linear-gradient(135deg, #f0ad4e 0%, #ffc107 100%);
  color: #1e3f70;
}

.plan-badge-family {
  background: linear-gradient(135deg, #28a745 0%, #5cb85c 100%);
  color: white;
}

/* Coverage Features */
.coverage-feature {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.25rem 0;
  font-size: 0.875rem;
}

/* Premium Box */
.premium-box {
  background: linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%);
  border-radius: 0.75rem;
  padding: 1.5rem;
}

/* Quick Action Buttons */
.quick-action-btn {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 1rem;
  background: #f8f9fa;
  border-radius: 0.75rem;
  text-decoration: none;
  color: #495057;
  transition: all 0.3s ease;
  text-align: center;
}

.quick-action-btn:hover {
  background: #2c5aa0;
  color: white;
  transform: translateY(-2px);
  box-shadow: 0 4px 12px rgba(44, 90, 160, 0.3);
}

.quick-action-btn i {
  font-size: 1.5rem;
  margin-bottom: 0.5rem;
}

.quick-action-btn span {
  font-size: 0.75rem;
  font-weight: 500;
}

/* Membership Card Preview */
.membership-card-preview {
  border: none;
  border-radius: 1rem;
  overflow: hidden;
}

.membership-card-inner {
  background: linear-gradient(135deg, #1e3f70 0%, #2c5aa0 50%, #4a90e2 100%);
  padding: 1.25rem;
}

.card-header-section {
  margin-bottom: 1rem;
}

.card-body-section {
  padding-top: 0.5rem;
}

/* Activity Timeline */
.activity-timeline {
  position: relative;
}

.activity-item {
  display: flex;
  align-items: flex-start;
  gap: 1rem;
  padding: 1rem 0;
  border-bottom: 1px solid #e9ecef;
}

.activity-item:last-child {
  border-bottom: none;
}

.activity-icon {
  width: 40px;
  height: 40px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}

.activity-icon i {
  font-size: 1rem;
}

.activity-content {
  flex-grow: 1;
}

.activity-amount {
  font-weight: 600;
}

/* Beneficiary Item */
.beneficiary-item {
  padding: 0.75rem;
  background: #f8f9fa;
  border-radius: 0.5rem;
  transition: all 0.2s ease;
}

.beneficiary-item:hover {
  background: #e9ecef;
}

/* Soft Buttons */
.btn-soft-primary {
  background-color: rgba(44, 90, 160, 0.1);
  color: #2c5aa0;
  border: none;
}

.btn-soft-primary:hover {
  background-color: #2c5aa0;
  color: #fff;
}

.btn-soft-success {
  background-color: rgba(40, 167, 69, 0.1);
  color: #28a745;
  border: none;
}

.btn-soft-success:hover {
  background-color: #28a745;
  color: #fff;
}

/* Text Utilities */
.text-white-50 {
  color: rgba(255, 255, 255, 0.5) !important;
}

/* Responsive */
@media (max-width: 768px) {
  .welcome-card .card-body {
    padding: 1rem;
  }

  .welcome-card h4 {
    font-size: 1.1rem;
  }

  .stat-icon {
    width: 40px;
    height: 40px;
    font-size: 1.25rem;
  }

  .quick-action-btn {
    padding: 0.75rem;
  }

  .quick-action-btn i {
    font-size: 1.25rem;
  }

  .membership-card-inner {
    padding: 1rem;
  }
}
</style>